import { useAccountSegment, usePendingSegment, capturePendingSegment } from './features/product-segment'
import { getProductConfig, productNavigation } from './app/product-config/registry'
import { captureOnboardingVariant, useOnboardingVariant, useDraftStore, draftVariant } from './features/onboarding'
import { useProductTheme } from './app/product-config/useProductTheme'
import ProductLoading from './app/product-config/ProductLoading'
import OnboardingRoute from './app/product-config/OnboardingRoute'
import { useEffect, useRef } from 'react'
import { Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom'
import AuthGuard from './components/AuthGuard'
import GuestOnly from './components/GuestOnly'
import AppShell from './components/AppShell'
import Login from './screens/Login/Login'
import Register from './screens/Register/Register'
import ForgotPassword from './screens/ForgotPassword/ForgotPassword'
import VerifyEmail from './screens/VerifyEmail/VerifyEmail'
import Welcome from './screens/Welcome/Welcome'
import Toaster from './components/Toaster'
import Lightbox from './components/Lightbox'
import InstallPrompt from './components/InstallPrompt'
import NotificationPrompt from './components/NotificationPrompt'
import InstallGuidePrompt from './components/InstallGuidePrompt'
import SystemBarsBackground from './components/SystemBarsBackground'
import { detectInviteToken, detectAccessToken, clearAccessToken } from './lib/invite'
import { detectYandexClientId, getYandexClientId } from './lib/ymUid'
import { useAuthStore } from './store/authStore'
import { useProfileStore } from './store/profileStore'
import { authApi } from './api/auth'
import { toast } from './store/toastStore'
import { useAnalyticsScreen } from './lib/analytics'

// Legacy .html links (index/register/forgot-password/verify-email) are still
// emitted by the shared backend — e.g. password-reset / verify emails point to
// `/forgot-password.html?token=...`. The multi-page vanilla app served those as
// real files; the SPA has extensionless routes, so without this the `.html`
// path falls through to the catch-all and bounces to `/` (losing the token).
// Redirect to the clean route, preserving the query string + hash.
function LegacyHtmlRedirect({ to }: { to: string }) {
  const { search, hash } = useLocation()
  return <Navigate to={`${to}${search}${hash}`} replace />
}

// Route map replaces the CSS display:none/flex screen toggling of the old app.
// Public auth routes + a guarded app shell with nested section routes.
export default function App() {
  const navigate = useNavigate()
  const location = useLocation()
  const account = useAccountSegment()
  const guestSegment = usePendingSegment((s) => s.segment)
  const guestVariant = useOnboardingVariant((s) => s.variant)
  const accountVariant = useDraftStore(({ draft }) => draft && draft.ownerUserId === account.profile?.userId ? draftVariant(draft.answers) : 'default')
  const config = account.authenticated ? (account.profile ? getProductConfig(account.profile.segment, accountVariant) : null) : getProductConfig(guestSegment, guestVariant)
  useProductTheme(config)
  useAnalyticsScreen(location.pathname, config?.id === 'break_up' && !account.authenticated && ['/welcome', '/login', '/register', '/forgot-password', '/verify-email'].includes(location.pathname), config?.id ?? '')
  useEffect(() => {
    if (!account.authenticated) {
      const url = new URL(window.location.href)
      captureOnboardingVariant(url, capturePendingSegment(url))
    }
  }, [location.search, account.authenticated])
  // StrictMode in dev double-fires effects; without this guard the redeem
  // would run twice (and we'd see two toasts). The token is also cleared from
  // localStorage at the end of the redeem, so this is mostly belt-and-suspenders
  // for the brief window before the async work finishes.
  const redeemingAccessRef = useRef(false)

  // Capture ?invite= and ?access= tokens once on load so register() can attach
  // them on the next /auth/register call. Both persist to localStorage and
  // strip themselves from the URL bar.
  // Also cache the Yandex Metrica `_ym_uid` cookie so the auth payload can
  // include it (feeds Direct Центр конверсий CSV, bd-glg).
  useEffect(() => {
    detectInviteToken()
    detectAccessToken()
    detectYandexClientId()
  }, [])

  // Sync the cached `_ym_uid` to the backend on boot for authenticated users
  // (bd-glg). Auth flows (login/register/OAuth) already send yandex_client_id,
  // but returning users with a cached JWT skip those — without this, their
  // ClientID never lands in the DB and Direct can't attribute their events.
  // Best-effort: silent on failure, fires once per app load.
  useEffect(() => {
    if (!useAuthStore.getState().isAuthenticated()) return
    const ymUid = getYandexClientId()
    if (!ymUid) return
    authApi.persistYandexClientId(ymUid).catch(() => {})
  }, [])

  // Logged-in user visiting `?access=<token>`: don't re-register, just redeem
  // the link against their existing account. Anonymous users fall through to
  // /register (which picks the token up from localStorage on submit).
  useEffect(() => {
    if (redeemingAccessRef.current) return
    const token = detectAccessToken()
    if (!token) return
    if (!useAuthStore.getState().isAuthenticated()) return
    redeemingAccessRef.current = true

    ;(async () => {
      try {
        const result = await authApi.redeemAccessLink(token)
        // Refresh profile stats so the paywall / token bar reflects the new sub.
        await useProfileStore.getState().loadStats(true)
        if (result.applied) {
          toast('Подписка активирована', 'success')
        } else if (result.status === 'already_redeemed') {
          toast('Эта ссылка уже была активирована', 'info')
        } else {
          // status === 'ok' but applied === false — already had an active sub.
          toast('У вас уже есть активная подписка', 'info')
        }
      } catch {
        // 404 → link missing/expired/exhausted. Anything else is unexpected.
        toast('Ссылка больше не действует', 'error')
      } finally {
        clearAccessToken()
        navigate('/', { replace: true })
      }
    })()
  }, [navigate])

  if (!config) return <ProductLoading error={account.error} retry={account.retry} />

  return (
    <>
    <SystemBarsBackground config={config} />
    <Routes>
      <Route path="/login" element={<GuestOnly><Login /></GuestOnly>} />
      <Route path="/register" element={<GuestOnly><Register /></GuestOnly>} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/verify-email" element={<VerifyEmail />} />
      <Route path="/welcome" element={<Welcome />} />
      <Route path="/onboarding" element={<OnboardingRoute key={`${account.profile?.userId ?? "guest"}:${config.id}:${config.onboarding.id}:${config.onboarding.version}:${config.onboarding.variant ?? 'default'}`} config={config} />} />

      {/* Legacy .html aliases from the shared backend (email links, old bookmarks). */}
      <Route path="/forgot-password.html" element={<LegacyHtmlRedirect to="/forgot-password" />} />
      <Route path="/verify-email.html" element={<LegacyHtmlRedirect to="/verify-email" />} />
      <Route path="/register.html" element={<LegacyHtmlRedirect to="/register" />} />
      <Route path="/index.html" element={<LegacyHtmlRedirect to="/" />} />

      <Route
        element={
          <AuthGuard>
            {account.profile && <AppShell scenario={config.onboarding} profile={account.profile} nav={productNavigation(config)} />}
          </AuthGuard>
        }
      >
        {config.sections.map(({ path, Component }) => <Route key={path} path={path} element={<Component />} />)}
      </Route>

      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
    <Toaster />
    <Lightbox />
    <InstallPrompt />
    <NotificationPrompt />
    <InstallGuidePrompt />
    </>
  )
}
